import React, { FC, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import { ResponseWeeklyMemberReport, ResponseWeeklyTeamReport } from '../../../../../states/types/report'
import Loading from '../../../common/Loading'
import Deviation from '../Deviation'
import DeviationHeader from '../DeviationHeader'
import WeeklyBarGraph from './WeeklyBarGraph'
import WeeklyJudgement from './WeeklyJudgement'
import { useTranslation } from 'react-i18next'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

type ScoreColumnName = 'stress_score' | 'focus_score' | 'efficiency_score'

interface Scores {
  label: ScoreColumnName
  current: number
  past: number | null
  company: number
  qasee?: number
  team?: number
  occupation?: number
  distributions: {
    from: number
    to: number
    percent: number
  }[]
}

interface Search {
  startDate: string
  endDate: string
  teamIds: number[]
  teamNames: string[]
  memberIds: number[]
  memberNames: string[]
  keywords: string[]
  serviceNames: string[]
  categoryIds: number[]
  categoryNames: string[]
  efficiencyLevel: string[]
  stressLevel: string[]
  focusLevel: string[]
  sortKey?: string,
  sortType?: string,
}

interface Props {
  target: string
  heatmapModalOpenCallback: (heatmap: Heatmap) => void,
}

const WeeklyBusinessEfficiency: FC<Props> = (props) => {
  const {
    target,
    heatmapModalOpenCallback,
  } = props

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const { t } = useTranslation()

  const subContentsRef = useRef<HTMLDivElement>(null)
  const subContentsLastRef = useRef<HTMLDivElement>(null)

  const [ruledLineHeight, setRuledLineHeight] = useState<number>(0)

  const [data, setData] = useState<ResponseWeeklyTeamReport | ResponseWeeklyMemberReport>()
  const [scores, setScores] = useState<Scores>()
  const [color, setColor] = useState<string>()
  const [workStyleId, setWorkStyleId] = useState<number>(0)
  const [occupationId, setOccupationId] = useState<number>(0)

  useEffect(() => {
    if (!subContentsRef.current?.offsetHeight || !subContentsLastRef.current?.offsetHeight) return
    setRuledLineHeight(subContentsRef.current?.offsetHeight - (subContentsLastRef.current?.offsetHeight / 2) + 40)
  }, [subContentsRef.current?.offsetHeight, subContentsLastRef.current?.offsetHeight])

  useEffect(() => {
    if (!target) return
    if (report.isLoading) return
    if (target === "team" && report.weeklyTeamReport) {
      setData(report.weeklyTeamReport)
      setScores(report.weeklyTeamReport.scores?.find((score) => score.label === "efficiency_score"))
      setColor("")
      setWorkStyleId(0)
      setOccupationId(0)
      return
    }
    if (target === "member" && report.weeklyMemberReport) {
      setData(report.weeklyMemberReport)
      setScores(report.weeklyMemberReport.scores?.find((score) => score.label === "efficiency_score"))
      setColor(report.weeklyMemberReport.member?.applied_occupation?.icon_color)
      setWorkStyleId(report.weeklyMemberReport.member?.applied_work_styles[0] ? report.weeklyMemberReport.member?.applied_work_styles[0].id : 0)
      setOccupationId(report.weeklyMemberReport.member?.applied_occupation?.id || 0)
    }
  }, [target, report])

  const targetIndicatorValues = (indicatorName: string) => ({
    current: data?.indicators?.find((value) => value.label === indicatorName)?.current ?? null,
    prev: data?.indicators?.find((value) => value.label === indicatorName)?.past ?? null,
    team: data?.indicators?.find((value) => value.label === indicatorName)?.team ?? null,
    company: data?.indicators?.find((value) => value.label === indicatorName)?.company ?? null,
    occupation: data?.indicators?.find((value) => value.label === indicatorName)?.occupation ?? null,
    qasee: data?.indicators?.find((value) => value.label === indicatorName)?.qasee ?? null,
    checked: data?.indicators?.find((value) => value.label === indicatorName)?.checked || false,
    appropriate_from: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_from ?? null,
    appropriate_to: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_to ?? null,
  })

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <>
            <section id="weekly_efficiency" className="page_contents">
              <div
                className="vertical_ruler"
                style={{
                  height: `${ruledLineHeight}px`,
                }}
              />
              <div id="efficiency" className="content-block weekly">
                <div className="block-detail score">
                  <div className="header_area">
                    <div className="title">
                      業務効率適正度
                    </div>
                    <div className="point">
                      {scores?.current || 0}
                    </div>
                    <div className="description">
                      勤務時間やPCの操作状況から、業務効率についてのチェックを行なっています。
                    </div>
                  </div>
                  <WeeklyBarGraph
                    target={target}
                    value={{
                      current: scores?.current || 0,
                      past: scores?.past || 0,
                      team: scores?.team,
                      occupation: scores?.occupation,
                      company: scores?.company || 0,
                      qasee: scores?.qasee,
                    }}
                  />
                </div>
              </div>
            </section>
            <section ref={subContentsRef}>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        適正度を構成する指標
                      </div>
                    </div>
                    <div className="block-detail">
                      <DeviationHeader
                        target={target}
                        range="weekly"
                        work_style_id={workStyleId}
                        occupation_id={occupationId}
                      />
                      <Deviation
                        target={target}
                        title="biz_per_active_time"
                        display_prefix="作業1時間あたりの"
                        display_title="操作量"
                        type="biz"
                        values={targetIndicatorValues("biz_per_active_time")}
                        labels={[0, 1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000]}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="shortcut_count_per_active_time"
                        display_prefix="作業1時間あたりの"
                        display_title="ショートカット回数"
                        type="count"
                        values={targetIndicatorValues("shortcut_count_per_active_time")}
                        labels={[0, 25, 50, 75, 100, 125, 150, 175, 200]}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="use_app_count"
                        display_title="利用アプリ数"
                        type="number"
                        values={targetIndicatorValues("use_app_count")}
                        labels={[0, 10, 20, 30, 40, 50, 60]}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        button={false}
                        displaySearchLink
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="app_switch_count_per_active_time"
                        display_prefix="作業1時間あたりの"
                        display_title="表示回数"
                        type="count"
                        values={targetIndicatorValues("app_switch_count_per_active_time")}
                        labels={[0, 50, 100, 150, 200, 250, 300, 350, 400]}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="overtime_work_time"
                        display_title="残業時間"
                        type="hour"
                        values={targetIndicatorValues("overtime_work_time")}
                        labels={[0, 4, 8, 12, 16]}
                        half
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        help_text={t(`report.indicator_help.weekly.${target}.efficiency.overtime_work_time`)}
                        color={color}
                      />
                    </div>
                  </div>
                </section>
              </section>
              <section
                className="inner_contents"
                ref={subContentsLastRef}
              >
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        作業効率判定
                      </div>
                    </div>
                    <div className="block-detail">
                      <WeeklyJudgement
                        target={target}
                        judgements={data?.judgements.efficiency_score_judgement}
                        score="efficiency"
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                      />
                    </div>
                  </div>
                </section>
              </section>
            </section>
          </>
        )
      }
    </>
  )
}

export default WeeklyBusinessEfficiency
